export const WAVEFORM_ANALYSER_FFT_SIZE = 256;
export const WAVEFORM_ANALYSER_SMOOTHING = 0.38;

export function configureWaveformAnalyser(
	analyser: AnalyserNode,
	smoothing = WAVEFORM_ANALYSER_SMOOTHING,
): void {
	analyser.fftSize = WAVEFORM_ANALYSER_FFT_SIZE;
	analyser.smoothingTimeConstant = smoothing;
}

/** Same mapping as the live rAF analyser path — keep precompute and playback visually identical. */
export function readFrequencyLevels(
	analyser: AnalyserNode,
	barCount: number,
): number[] {
	const bufferLength = analyser.frequencyBinCount;
	const dataArray = new Uint8Array(bufferLength);
	analyser.getByteFrequencyData(dataArray);

	const step = Math.max(1, Math.floor(bufferLength / barCount));
	const levels: number[] = [];

	for (let index = 0; index < barCount; index += 1) {
		let peak = 0;
		for (let offset = 0; offset < step; offset += 1) {
			peak = Math.max(peak, dataArray[index * step + offset] ?? 0);
		}
		const normalized = peak / 255;
		levels.push(0.15 + normalized * 0.85);
	}

	return levels;
}

/**
 * Peak amplitude per bar from PCM — used to bake waveform timelines without a
 * realtime AudioContext (preload decode runs before any user gesture).
 */
export function readPcmPeakLevels(
	channelData: Float32Array,
	startSample: number,
	lengthSamples: number,
	barCount: number,
): number[] {
	const count = Math.max(1, Math.floor(barCount));
	const windowLength = Math.max(0, lengthSamples);
	const levels: number[] = [];
	for (let index = 0; index < count; index += 1) {
		const start =
			startSample + Math.floor((index / count) * windowLength);
		const end =
			startSample + Math.floor(((index + 1) / count) * windowLength);
		const sampleEnd = Math.max(start + 1, end);
		let peak = 0;
		for (let sample = start; sample < sampleEnd; sample += 1) {
			const amplitude = Math.abs(channelData[sample] ?? 0);
			if (amplitude > peak) {
				peak = amplitude;
			}
		}
		levels.push(0.15 + Math.min(1, peak) * 0.85);
	}
	return levels;
}

/** Collapse or expand a timeline/analyser frame to the rendered bar count. */
export function resampleWaveformLevels(
	levels: readonly number[],
	barCount: number,
): number[] {
	const count = Math.max(0, Math.floor(barCount));
	if (count === 0) {
		return [];
	}
	if (levels.length === 0) {
		return Array.from({ length: count }, () => 0.15);
	}
	if (levels.length === count) {
		return [...levels];
	}

	const result: number[] = [];
	for (let index = 0; index < count; index += 1) {
		const start = Math.floor((index / count) * levels.length);
		const end = Math.max(
			start + 1,
			Math.floor(((index + 1) / count) * levels.length),
		);
		let peak = 0;
		for (let cursor = start; cursor < end; cursor += 1) {
			peak = Math.max(peak, levels[cursor] ?? 0);
		}
		result.push(peak);
	}
	return result;
}

/** Blend analyser envelope with seed profile so bars keep a wave silhouette while pulsing. */
export function blendWaveformBarHeights(
	liveLevels: number[],
	baseHeights: number[],
): number[] {
	return liveLevels.map((liveLevel, index) => {
		const baseLevel = baseHeights[index] ?? 0.15;
		const envelope = Math.max(0.15, liveLevel);
		const profile = 0.35 + 0.65 * baseLevel;
		return 0.15 + (envelope - 0.15) * profile;
	});
}

/** Match AnalyserNode smoothing between consecutive timeline frames (audio-time, not wall-clock). */
export function smoothWaveformTimelineFrames(
	frames: number[][],
	smoothing = WAVEFORM_ANALYSER_SMOOTHING,
): number[][] {
	if (frames.length === 0) {
		return frames;
	}

	const smoothed: number[][] = [frames[0] ?? []];

	for (let index = 1; index < frames.length; index += 1) {
		const previousFrame = smoothed[index - 1] ?? [];
		const currentFrame = frames[index] ?? [];
		smoothed.push(
			currentFrame.map((level, barIndex) => {
				const previousLevel = previousFrame[barIndex] ?? level;
				return smoothing * previousLevel + (1 - smoothing) * level;
			}),
		);
	}

	return smoothed;
}
